import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { CompareItemsSelectedItem, CompareItemsSelectedItems, CompareItemsProps } from '../types'

export const SelectedItemsContext = createContext<CompareItemsSelectedItems>({
  items: [],
  setSelectedIndexes: () => void 0,
  presetIndex: -1,
  setPresetIndex: () => void 0,
  sortByAttributeIndex: -1,
  reverseSortOrder: false,
  setSortByAttribute: () => void 0,
  addItem: () => void 0,
  changeItem: () => void 0,
  removeItem: () => void 0,
  onClickItem: null
})

export default function useSelectedItems(): CompareItemsSelectedItems {
  const selectedItems = useContext(SelectedItemsContext)
  return selectedItems
}

export function useSelectedItemsState(props: CompareItemsProps): CompareItemsSelectedItems {
  const {
    items,
    attributes,
    selectedIndexes: props_selectedIndexes,
    presetIndex: props_presetIndex,
    presets,
    sortByAttributeIndex: props_sortByAttributeIndex,
    reverseSortOrder: props_reverseSortOrder = false,
    onSelectedItemsUpdated
  } = props

  // read through a ref so a host that hands a new function every render does not rebuild the selection
  const onClickItemRef = useRef(props.onClickItem)
  onClickItemRef.current = props.onClickItem
  const canClickItem = 'function' === typeof props.onClickItem

  // managed states
  const [selectedIndexes, setSelectedIndexes] = useState<number[]>(props_selectedIndexes)
  const [presetIndex, setPresetIndex] = useState<number>(props_presetIndex)
  const [{ sortByAttributeIndex, reverseSortOrder }, setSortByAttribute] = useState<{
    sortByAttributeIndex: number
    reverseSortOrder: boolean
  }>(() => {
    return {
      sortByAttributeIndex: props_sortByAttributeIndex,
      reverseSortOrder: Boolean(props_reverseSortOrder)
    }
  })
  // sync managed states
  const lastProps = useRef<Partial<CompareItemsProps>>()
  if (null == lastProps.current) {
    lastProps.current = {
      selectedIndexes: props_selectedIndexes,
      presetIndex: props_presetIndex,
      sortByAttributeIndex: props_sortByAttributeIndex,
      reverseSortOrder: props_reverseSortOrder
    }
  }
  useEffect(() => {
    if (lastProps.current.selectedIndexes !== props_selectedIndexes) {
      lastProps.current.selectedIndexes = props_selectedIndexes
      setSelectedIndexes(props_selectedIndexes)
    }
    if (lastProps.current.presetIndex !== props_presetIndex) {
      lastProps.current.presetIndex = props_presetIndex
      setPresetIndex(props_presetIndex)
    }
    if (
      lastProps.current.sortByAttributeIndex !== props_sortByAttributeIndex ||
      lastProps.current.reverseSortOrder !== props_reverseSortOrder
    ) {
      lastProps.current.sortByAttributeIndex = props_sortByAttributeIndex
      lastProps.current.reverseSortOrder = props_reverseSortOrder
      setSortByAttribute({
        sortByAttributeIndex: props_sortByAttributeIndex,
        reverseSortOrder: Boolean(props_reverseSortOrder)
      })
    }
  }, [props_selectedIndexes, props_presetIndex, props_sortByAttributeIndex, props_reverseSortOrder])

  // safe callbacks ref
  const callbackRefs = useRef<{
    onSelectedItemsUpdated?: CompareItemsProps['onSelectedItemsUpdated']
  }>({})
  callbackRefs.current.onSelectedItemsUpdated = onSelectedItemsUpdated

  // selected items state
  const selectedItems: CompareItemsSelectedItems = useMemo(() => {
    const selectedItems: CompareItemsSelectedItem[] = []

    if (Array.isArray(selectedIndexes)) {
      // If selectedIndexes is provided, use it to filter items
      for (const index of selectedIndexes) {
        const item = items[index]
        if (item) {
          selectedItems.push({ ...item, _index: index })
        }
      }
    } else if ('number' === typeof presetIndex && 0 <= presetIndex && null != presets?.[presetIndex]?.indexes) {
      // If presetIndex is provided, use the corresponding preset to filter items
      const presetSelectedIndexes = presets[presetIndex].indexes(
        items,
        attributes,
        sortByAttributeIndex,
        reverseSortOrder || false
      )
      if (Array.isArray(presetSelectedIndexes)) {
        for (const index of presetSelectedIndexes) {
          const item = items[index]
          if (item) {
            selectedItems.push({ ...item, _index: index })
          }
        }
      }
    } else {
      // If no selectedIndexes or presetIndex is provided, return all items
      for (let index = 0; index < items.length; index++) {
        const item = items[index]
        selectedItems.push({ ...item, _index: index })
      }
    }

    // If sortByAttributeIndex is provided, sort items by that attribute
    if (
      'number' === typeof sortByAttributeIndex &&
      0 <= sortByAttributeIndex &&
      null != attributes?.[sortByAttributeIndex]
    ) {
      selectedItems.sort((a, b) => {
        const aAttribute = a.attributes?.[sortByAttributeIndex]
        const bAttribute = b.attributes?.[sortByAttributeIndex]
        const aPercents = 'number' === typeof aAttribute?.percents ? aAttribute.percents : 0
        const bPercents = 'number' === typeof bAttribute?.percents ? bAttribute.percents : 0
        const aAmount = 'number' === typeof aAttribute?.amount ? aAttribute.amount : 0
        const bAmount = 'number' === typeof bAttribute?.amount ? bAttribute.amount : 0

        const percentsComparison = reverseSortOrder ? aPercents - bPercents : bPercents - aPercents
        if (0 !== percentsComparison) {
          return Math.sign(percentsComparison)
        } else {
          const amountComparison = reverseSortOrder ? aAmount - bAmount : bAmount - aAmount
          return Math.sign(amountComparison)
        }
      })
    }

    const selectedItemsState: CompareItemsSelectedItems = {
      items: selectedItems,
      setSelectedIndexes: (newSelectedIndexes, matchPreset = false) => {
        let updatedSelectedIndexes = newSelectedIndexes
        let updatedPresetIndex = -1
        // search for preset with supplied indexes
        if (matchPreset && Array.isArray(newSelectedIndexes)) {
          const presetIndex = presets?.findIndex(preset => {
            const presetSelectedIndexes = preset.indexes(
              items,
              attributes,
              sortByAttributeIndex,
              reverseSortOrder || false
            )
            return (
              newSelectedIndexes.length === presetSelectedIndexes.length &&
              newSelectedIndexes.every((index, i) => index === presetSelectedIndexes[i])
            )
          })
          if (0 <= presetIndex) {
            updatedSelectedIndexes = null
            updatedPresetIndex = presetIndex
          }
        }
        const matched = 0 <= updatedPresetIndex
        const updatedSortByAttributeIndex: number = matched ? sortByAttributeIndex : -1
        const updatedReverseSortOrder = matched ? reverseSortOrder || false : false

        setSelectedIndexes(updatedSelectedIndexes)
        setPresetIndex(updatedPresetIndex)
        setSortByAttribute({
          sortByAttributeIndex: updatedSortByAttributeIndex,
          reverseSortOrder: updatedReverseSortOrder
        })

        callbackRefs.current.onSelectedItemsUpdated?.(
          {
            selectedIndexes: updatedSelectedIndexes,
            presetIndex: updatedPresetIndex,
            sortByAttributeIndex: updatedSortByAttributeIndex,
            reverseSortOrder: updatedReverseSortOrder
          },
          {
            items,
            presets,
            attributes
          }
        )
      },

      presetIndex,
      setPresetIndex: newPresetIndex => {
        const updatedSelectedIndexes: number[] = null
        const updatedPresetIndex = newPresetIndex
        const updatedSortByAttributeIndex = sortByAttributeIndex
        const updatedReverseSortOrder = reverseSortOrder

        setSelectedIndexes(updatedSelectedIndexes)
        setPresetIndex(updatedPresetIndex)
        setSortByAttribute({
          sortByAttributeIndex: updatedSortByAttributeIndex,
          reverseSortOrder: updatedReverseSortOrder
        })

        callbackRefs.current.onSelectedItemsUpdated?.(
          {
            selectedIndexes: updatedSelectedIndexes,
            presetIndex: updatedPresetIndex,
            sortByAttributeIndex: updatedSortByAttributeIndex,
            reverseSortOrder: updatedReverseSortOrder
          },
          {
            items,
            presets,
            attributes
          }
        )
      },

      sortByAttributeIndex,
      reverseSortOrder,
      setSortByAttribute: (newSortByAttributeIndex: number, newReverseSortOrder: boolean = false) => {
        const updatedSelectedIndexes = selectedIndexes
        const updatedPresetIndex = presetIndex
        const updatedSortByAttributeIndex = newSortByAttributeIndex
        const updatedReverseSortOrder = newReverseSortOrder

        setSelectedIndexes(updatedSelectedIndexes)
        setPresetIndex(updatedPresetIndex)
        setSortByAttribute({
          sortByAttributeIndex: updatedSortByAttributeIndex,
          reverseSortOrder: updatedReverseSortOrder
        })

        callbackRefs.current.onSelectedItemsUpdated?.(
          {
            selectedIndexes: updatedSelectedIndexes,
            presetIndex: updatedPresetIndex,
            sortByAttributeIndex: updatedSortByAttributeIndex,
            reverseSortOrder: updatedReverseSortOrder
          },
          {
            items,
            presets,
            attributes
          }
        )
      },

      addItem: itemIndex => {
        if (null != items[itemIndex]) {
          // add item to selectedIndexes
          const newSelectedIndexes = Array.isArray(selectedIndexes)
            ? [...selectedIndexes, itemIndex]
            : [...selectedItems.map(item => item._index), itemIndex]
          selectedItemsState.setSelectedIndexes(newSelectedIndexes, true)
        }
      },
      changeItem: (selectedItemIndex, newItemIndex) => {
        if (null != selectedItems[selectedItemIndex] && null != items[newItemIndex]) {
          const newSelectedIndexes = [
            ...selectedItems.slice(0, selectedItemIndex),
            { ...items[newItemIndex], _index: newItemIndex },
            ...selectedItems.slice(selectedItemIndex + 1)
          ].map(item => item._index)
          selectedItemsState.setSelectedIndexes(newSelectedIndexes, true)
        }
      },
      removeItem: selectedItemIndex => {
        // remove item from selectedIndexes
        if (null != selectedItems[selectedItemIndex]) {
          const newSelectedIndexes = [
            ...selectedItems.slice(0, selectedItemIndex),
            ...selectedItems.slice(selectedItemIndex + 1)
          ].map(item => item._index)
          selectedItemsState.setSelectedIndexes(newSelectedIndexes, true)
        }
      },
      onClickItem: canClickItem
        ? selectedItemIndex => {
            const item = selectedItems[selectedItemIndex]
            if (null != item) onClickItemRef.current?.(item._index, items[item._index], items)
          }
        : null
    }

    return selectedItemsState
  }, [selectedIndexes, presetIndex, presets, items, attributes, sortByAttributeIndex, reverseSortOrder, canClickItem])

  return selectedItems
}
